import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Users, UserPlus, Search, Mail, Shield, DollarSign,
  Building2, MoreVertical, Edit, Trash
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const ROLES = [
  { value: 'buyer', label: 'Acheteur', color: 'bg-blue-100 text-blue-800' },
  { value: 'manager', label: 'Manager', color: 'bg-purple-100 text-purple-800' },
  { value: 'finance', label: 'Finance', color: 'bg-green-100 text-green-800' },
  { value: 'admin', label: 'Admin', color: 'bg-orange-100 text-orange-800' },
  { value: 'giftcard_admin', label: 'Admin Giftcard', color: 'bg-pink-100 text-pink-800' },
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-800' },
];

export const EnterpriseUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
    password: "",
    role: "buyer",
    department: "",
    budget_limit: 100000
  });
  const [inviting, setInviting] = useState(false);

  const token = localStorage.getItem('enterprise_token');
  const currentUser = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    fetchUsers();
  }, [token, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 403) {
        toast.error("Ou pa gen aksè pou wè itilizatè yo");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      const response = await axios.post(`${API}/enterprise/users/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Envitasyon voye pou ${inviteData.email}`);
        setShowInviteModal(false);
        setInviteData({ email: "", name: "", password: "", role: "buyer", department: "", budget_limit: 100000 });
        fetchUsers();
        
        // Show temp password (in production this would be sent via email)
        if (response.data.temp_password) {
          toast.info(`Modpas tanporè: ${response.data.temp_password}`, { duration: 10000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan envitasyon an");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/enterprise/users/${userId}/role?role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Wòl itilizatè a chanje");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan chanjman wòl");
    }
  };

  const getRoleBadge = (role) => {
    const roleInfo = ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${roleInfo.color}`}>
        {roleInfo.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canManageUsers = ['admin', 'super_admin'].includes(currentUser.role);

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-users">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">Itilizatè</h1>
            <p className="text-gray-500">Jere itilizatè antrepriz la</p>
          </div>
          
          {canManageUsers && (
            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold"
              data-testid="invite-user-btn"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Kreye Itilizatè
            </Button>
          )}
        </div>

        {/* Search */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Chèche pa non oswa imèl..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-semibold text-gray-600">Pa gen itilizatè</p>
              <p className="text-gray-400">Envite moun nan ekip ou a</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map((user) => (
              <Card key={user.id} className="border-0 shadow-lg" data-testid={`user-${user.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#FF9900] flex items-center justify-center text-black font-bold text-lg">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#232F3E]">{user.name}</span>
                          {getRoleBadge(user.role)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {user.email}
                          </span>
                          {user.department && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {user.department}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Limit Bidjè</p>
                        <p className="font-bold text-[#FF9900]">
                          {user.budget_limit?.toLocaleString()} HTG
                        </p>
                      </div>
                      
                      {canManageUsers && user.id !== currentUser.id && (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className="border rounded-lg px-3 py-2 text-sm"
                          data-testid={`role-select-${user.id}`}
                        >
                          {ROLES.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#FF9900]" />
                  Kreye Nouvo Itilizatè
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Non Konplè</Label>
                    <Input
                      value={inviteData.name}
                      onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                      placeholder="Jean Baptiste"
                      required
                      data-testid="invite-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Imèl (Username)</Label>
                    <Input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                      placeholder="jean@antrepriz.ht"
                      required
                      data-testid="invite-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Modpas (Password)</Label>
                    <Input
                      type="password"
                      value={inviteData.password}
                      onChange={(e) => setInviteData({...inviteData, password: e.target.value})}
                      placeholder="Antre modpas"
                      required
                      minLength={6}
                      data-testid="invite-password"
                    />
                    <p className="text-xs text-gray-500">Minimòm 6 karaktè</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Wòl</Label>
                    <select
                      value={inviteData.role}
                      onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      data-testid="invite-role"
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Depatman (Opsyonèl)</Label>
                    <Input
                      value={inviteData.department}
                      onChange={(e) => setInviteData({...inviteData, department: e.target.value})}
                      placeholder="IT, Marketing, etc."
                      data-testid="invite-department"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Limit Bidjè (HTG)</Label>
                    <Input
                      type="number"
                      value={inviteData.budget_limit}
                      onChange={(e) => setInviteData({...inviteData, budget_limit: parseInt(e.target.value)})}
                      data-testid="invite-budget"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowInviteModal(false)}
                      className="flex-1"
                    >
                      Anile
                    </Button>
                    <Button
                      type="submit"
                      disabled={inviting}
                      className="flex-1 bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    >
                      {inviting ? "Ap kreye..." : "Kreye Itilizatè"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

